import {Col, Layout, Row,} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import {Contain} from "@components/layout";
import {Helmet as Head} from "react-helmet-async";
import {getWordpressVars} from "@lib/wordpressVars";
import useIsLoggedIn from "@hooks/useIsLoggedIn";
import {useGetSeriesByIdQuery} from "@mwServices/Series";
import usePreferences from "@hooks/usePreferences";
import {IssueFiltersPaginatedQuery, useGetIssuesQuery} from "@mwServices/Issue";
import {useWindowSize} from "@components/window-size-provider";
import useSubscriptionType from "@hooks/useSubscriptionType";
import SeriesFiltersDrawer from "@mwPages/series/SereisFiltersDrawer";
import {formatNumber, formatTitleCase} from "@lib/format";
import {useWishlistAddIssueMutation, useWishlistAddIssuesFromFiltersMutation} from "@mwServices/User";
import {showResponse} from "@services/Api";
import Paragraph from "antd/lib/typography/Paragraph";
import IssuesResults from "@mwPages/issues/IssuesResults";

const SeriesPage = () => {
    const windowDimensions = useWindowSize();
    const seriesId = getWordpressVars().ID;
    const isLoggedIn = useIsLoggedIn();
    const { showListView, setShowListView, itemCount, setItemCount,  showListViewNoImages, setShowListViewNoImages} = usePreferences();
    const subscriptionType = useSubscriptionType();
    const [showEditMultiple, setShowEditMultiple] = useState(false);
    const [selectedComicIds, setSelectedComicIds] = useState<number[]>([]);
    const [showSearch, setShowSearch] = useState(false);
    const [search, setSearch] = useState('');

    const seriesQuery = useGetSeriesByIdQuery({seriesId});
    const seriesData = useMemo(() => {
        if(seriesQuery && seriesQuery.data && seriesQuery.data.series) {
            const newData = {...seriesQuery.data.series};
            if(newData.series_title.toLowerCase().indexOf('the') === 0) {
                newData.series_title = newData.series_title.substr(4);
            }
            newData.publisher = newData.publisher ? formatTitleCase(newData.publisher) : 'Unknown Publisher';
            if(newData.publisher === 'Dc') newData.publisher = 'DC';
            return newData;
        }
        return {
            series_title: '',
            full_image: '',
            publisher: '',
            year_began: '',
            year_ended: '',
            issue_count: 0,
            issue_min: '',
            issue_max: '',
            overview: {
                total: 0,
                issues: 0,
                items: 0,
                items_hot: 0,
                items_key: 0,
                items_rare: 0,
                issues_by_number: 0,
            },
        };
    }, [seriesQuery]);

    const defaultFilters = useMemo(() => ({
        query: '',
        series: [seriesId],
        sort: 26,
        showVariants: false, //This means include variants in the results, not only variants.
        inCollection: false,
        isHot: false,
        isKey: false,
        isRare: false,
        multipleDateRanges: undefined,
    }), [seriesId]);

    const [issueParams, setIssueParams] = useState<IssueFiltersPaginatedQuery>({
        filters: { ...defaultFilters },
        page: 1,
        perPage: itemCount,
    });

    useEffect(() => {
        if(search !== issueParams.filters.query){
            setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, query: search ? search : '' } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        if(issueParams.filters.query && search !== issueParams.filters.query){
            setSearch(issueParams.filters.query);
        }
    }, [issueParams.filters.query]);

    useEffect(() => {
        setIssueParams({
            filters: { ...defaultFilters },
            page: 1,
            perPage: itemCount,
        });
    }, [defaultFilters]);

    const issueQuery = useGetIssuesQuery(issueParams);

    const {pagination} = useMemo(() => {
        if(issueQuery.data) {
            return { pagination: issueQuery.data.pagination }
        }
        return { pagination: null};
    }, [issueQuery]);

    const issueCount = useMemo(() => (issueQuery.data) ? issueQuery.data.issues.length : 0, [issueQuery]);

    const [addToCollectionIds, setAddToCollectionIds] = useState<number[]>([]);

    const showValueRibbon = useMemo(() => (
            subscriptionType.isStandard ||
            subscriptionType.isTrial ||
            subscriptionType.isUnlimited),
        [subscriptionType]
    );

    const showDesktopView = useMemo(() => {
        return windowDimensions.over(799);
    }, [windowDimensions]);

    const toggleSelected = (id: number) => {
        setSelectedComicIds(currentIds => {
            if(currentIds.indexOf(id) === -1) {
                return [...currentIds, id];
            } else {
                return currentIds.filter((item) => item !== id);
            }
        })
    };

    const [selectAllType, setSelectAllType] = useState('page');
    useEffect(() => {
        if(selectAllType === 'all' && issueQuery.data){
            setSelectedComicIds(issueQuery.data.issues.map(issue => issue.id) as number[]);
        }
    }, [selectAllType, issueQuery.data]);

    //If user deselects any of the issues on this page, and the select type is 'all', revert to 'page'
    useEffect(() => {
        if(selectAllType === 'all'){
            if(issueQuery.data && selectedComicIds.length !== issueQuery.data.issues.length){
                setSelectAllType('page');
            }
        }
    }, [selectedComicIds]);

    const [wishlistIssueAdd, addWishlistResponse] = useWishlistAddIssueMutation();
    useEffect(() => {
        // if (addWishlistResponse.isSuccess) onIssuesUpdateBulk(selectedComicIds, {in_wishlist: true});
        showResponse(addWishlistResponse, 'Issues added to wishlist.', 'Failed to add issues to wishlist.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addWishlistResponse]);

    const [wishlistIssueAddByFilters, addWishlistByFiltersResponse] = useWishlistAddIssuesFromFiltersMutation();
    useEffect(() => {
        // if (addWishlistResponse.isSuccess) onIssuesUpdateBulk(selectedComicIds, {in_wishlist: true});
        showResponse(addWishlistByFiltersResponse, 'Issues added to wishlist.', 'Failed to add issues to wishlist.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addWishlistByFiltersResponse]);

    const smallButtons = useMemo(() => {
        return windowDimensions.under(1200);
    }, [windowDimensions]);

    return (
    <Layout className="page series-page">
      <Head>
        <title>{seriesData.series_title} - CovrPrice</title>
      </Head>
        <div
            className="series-header"
            style={{backgroundImage: `linear-gradient( to top right, rgba(14, 32, 45, 1), rgba(14, 32, 45, 0.5) ),url(${seriesData && seriesData.full_image})`}}
        >
            <Row>
                <Col span={24} className={"header-content"}>
                    <Paragraph className={"heading-text"} ellipsis={{ rows: 1 }}>{seriesData && seriesData.series_title}</Paragraph>
                    <Paragraph className={"sub-heading-text"} ellipsis={{ rows: 3 }}>
                        {seriesData.series_title !== '' && (
                            seriesData.publisher + ' | ' +
                            seriesData.year_began + (seriesData.year_ended && seriesData.year_ended !== seriesData.year_began ? ' - ' + seriesData.year_ended : '')  + ' | ' +
                            '#' + seriesData.issue_min + (seriesData.issue_max ? '-' + seriesData.issue_max : ''))}
                    </Paragraph>
                </Col>
            </Row>
        </div>
        <Contain>
            <IssuesResults
                issueQuery={issueQuery}
                issuesTitle={<span>{formatNumber(seriesData.issue_count)} Issues</span>}
                infoModalData={{
                    title: "Series Page",
                    content: (
                        <p>The Series Page on CovrPrice lets you see info about each comic's Top
                            Estimate Value, Current Market Trends, Most Recent Sales, and more. If
                            you wish to add multiple items (or the entire series) to your CovrPrice
                            collection select the "+Add" button. </p>
                    ),
                }}
                defaultFilters={defaultFilters}
                issueParams={issueParams}
                setIssueParams={setIssueParams}
                search={search}
                setSearch={setSearch}
                selectedComicIds={selectedComicIds}
                setSelectedComicIds={setSelectedComicIds}
                filterDrawer={
                    <SeriesFiltersDrawer
                        filters={issueParams.filters}
                        setFilters={(filters) => setIssueParams({...issueParams, page: 1, filters})}
                        defaultFilters={defaultFilters}
                        showListView={showListView}
                        showListViewNoImages={showListViewNoImages}
                        setShowListViewNoImages={setShowListViewNoImages}
                    />
                }
                isSearchBarcode={false}
                isSearchES={false}
                useGridView={true}
                useListView={true}
                useListViewNoImages={true}
                showIssueNotes={false}
            />
        </Contain>
    </Layout>
    );
};

export default SeriesPage;
